// Offline indicator component for mobile app
import React from 'react';
import { WifiOff, Cloud, CloudOff, RefreshCw, Check } from 'lucide-react';
import { useOffline } from '../hooks/useOffline';

export function OfflineIndicator({ className = '' }) {
  const { isOffline, pendingActions, triggerSync } = useOffline();

  if (!isOffline && pendingActions === 0) {
    return null;
  }

  return (
    <div className={`offline-indicator ${className}`}>
      {isOffline ? (
        <div className="offline-banner offline-banner-offline">
          <WifiOff size={16} />
          <span>Du är offline</span>
        </div>
      ) : pendingActions > 0 ? (
        <div className="offline-banner offline-banner-pending" onClick={triggerSync}>
          <Cloud size={16} />
          <span>{pendingActions} väntande ändringar</span>
          <RefreshCw size={14} className="ml-1" />
        </div>
      ) : null}
    </div>
  );
}

// Floating offline status badge
export function OfflineBadge() {
  const { isOffline, pendingActions } = useOffline();

  if (!isOffline && pendingActions === 0) {
    return null;
  }

  return (
    <div className="offline-badge">
      {isOffline ? (
        <CloudOff size={18} className="text-red-500" />
      ) : (
        <div className="relative">
          <Cloud size={18} className="text-yellow-500" />
          <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
            {pendingActions}
          </span>
        </div>
      )}
    </div>
  );
}

// Sync status toast
export function SyncStatus({ show, success, message, onClose }) {
  if (!show) return null;

  return (
    <div className={`sync-toast ${success ? 'sync-toast-success' : 'sync-toast-error'}`}>
      {success ? <Check size={16} /> : <CloudOff size={16} />}
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">×</button>
    </div>
  );
}

// Offline wrapper - shows offline state for a section
export function OfflineWrapper({ children, fallback, requireOnline = false }) {
  const { isOffline } = useOffline();

  if (isOffline && requireOnline) {
    return fallback || (
      <div className="offline-fallback">
        <CloudOff size={48} className="text-slate-400 mb-4" />
        <h3 className="text-lg font-medium text-slate-700 mb-2">
          Inte tillgängligt offline
        </h3>
        <p className="text-slate-500 text-sm">
          Den här funktionen kräver internetanslutning.
        </p>
      </div>
    );
  }

  return children;
}

// Stale data indicator
export function StaleDataIndicator({ isStale, onRefresh, loading }) {
  const { isOnline } = useOffline();

  if (!isStale || !isOnline) return null;

  return (
    <div className="stale-indicator" onClick={onRefresh}>
      <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
      <span>Data kan vara föråldrad. Tryck för att uppdatera.</span>
    </div>
  );
}

export default OfflineIndicator;
